import React, { createContext, useContext, useState, useEffect, useMemo, ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { AuthCreateDto, AuthLoginDto, AuthVerifyOtpDto } from '@libs/dto';
import { authApi } from '@carcareers/store';
import { routes, SessionAuth, isEmpty } from '@carcareers/utils';
import { useLoading } from './LoadingContext';
import { useLocalStorage } from '../hooks/useLocalStorage';
import { AuthRole } from '@libs/types';

interface AuthContextProps {
  session: SessionAuth;
  user: SessionAuth['user'];
  setSession: React.Dispatch<React.SetStateAction<SessionAuth>>;
  signIn: (email?: string, password?: string, redirectTo?: string | null) => Promise<string | void>;
  signOut: () => Promise<void>;
  createAccount: (auth: AuthCreateDto) => Promise<any>;
  verifyOTP: (email: string, otp: string) => Promise<any>;
  refreshToken: () => Promise<boolean>;
  clearSession: () => void;
}

const AuthContext = createContext<AuthContextProps | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const navigate = useNavigate();
  const { setLoading, setLabel } = useLoading();
  const [authLogin] = authApi.useAuthLoginMutation();
  const [authLogout] = authApi.useAuthLogoutMutation();
  const [authRefresh] = authApi.useAuthRefreshMutation();
  const [authCreate] = authApi.useAuthCreateMutation();
  const [authVerifyOTP] = authApi.useAuthVerifyOTPMutation();
  const [authCheckMigrated] = authApi.useAuthCheckMigratedMutation();
  const [localStorage, setLocalStorage] = useLocalStorage<SessionAuth>('session', {});
  const [postId, setPostId] = useLocalStorage<number | undefined>('post_id', undefined, true);
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect_to');

  const [session, setSession] = useState<SessionAuth>(() => {
    if (localStorage.auth?.token && localStorage.auth?.expires && new Date(localStorage.auth.expires) > new Date()) {
      return localStorage;
    }

    return {};
  });

  const clearSession = () => {
    setSession({});
    setLocalStorage({});
  };

  const refreshToken = async (): Promise<boolean> => {
    try {
      const currentRefreshToken = session.auth?.refreshToken || localStorage.auth?.refreshToken;
      const refreshExpires = session.auth?.refreshExpires || localStorage.auth?.refreshExpires;

      if (!currentRefreshToken || !refreshExpires) {
        return false;
      }

      // Check if refresh token is still valid
      if (new Date(refreshExpires) <= new Date()) {
        clearSession();
        return false;
      }

      const response = await authRefresh({ refreshToken: currentRefreshToken });

      if (response.data) {
        const updatedSession = {
          ...session,
          auth: {
            token: response.data.token,
            expires: new Date(response.data.expiresAt ?? new Date()),
            refreshToken: response.data.refreshToken || currentRefreshToken,
            refreshExpires: new Date(response.data.refreshExpiresAt ?? refreshExpires),
          },
        };

        setSession(updatedSession);
        setLocalStorage(updatedSession);
        return true;
      }

      return false;
    } catch (error) {
      console.error('Token refresh failed:', error);
      clearSession();
      return false;
    }
  };

  // Listen for changes to localStorage and update session state
  useEffect(() => {
    if (localStorage.auth?.token && localStorage.auth?.expires && new Date(localStorage.auth.expires) > new Date()) {
      setSession(localStorage);
    } else if (isEmpty(localStorage) && !isEmpty(session)) {
      setSession({});
    }
  }, [localStorage, session]);

  // Clean up expired sessions on app load
  useEffect(() => {
    if (session.auth?.expires && new Date(session.auth.expires) <= new Date()) {
      clearSession();
    }
  }, []);

  const redirectToRole = (role: AuthRole) => {
    switch (role) {
      case AuthRole.JOBSEEKER:
        navigate(`/${routes.JOBSEEKERS}/${postId ?? ''}`);
        setPostId(undefined);
        break;
      case AuthRole.EMPLOYER:
        navigate(`/${routes.EMPLOYER_HOME}`);
        break;
      case AuthRole.ADMIN:
        navigate(`/${routes.ADMIN_HOME}`);
        break;
      default:
        navigate(`/${routes.LOGIN}`);
        break;
    }
  };

  const value = useMemo(
    () => ({
      session,
      user: session.user,
      setSession,
      clearSession,
      signIn: async (email?: string, password?: string) => {
        if (email && password) {
          const { data: isMigrated } = await authCheckMigrated({ email });

          if (isMigrated) {
            navigate(`${routes.FORGOT_PASSWORD}?email=${email}`, { state: { isMigrated } });
            return;
          }

          const response = await authLogin({ email, password } as AuthLoginDto);

          // Handle errors first before any navigation
          if (response.error) {
            return 'Invalid email or password';
          }

          if (response.data && !response.data.isExternalApply) {
            const session = {
              user: response.data.user,
              profile: response.data.profile,
              auth: {
                token: response.data.token,
                expires: new Date(response.data.expiresAt ?? new Date()),
                refreshToken: response.data.refreshToken,
                refreshExpires: new Date(response.data.refreshExpiresAt ?? new Date()),
              },
            };

            setSession(session);
            setLocalStorage(session);

            // Set session storage flag to indicate user just logged in
            sessionStorage.setItem('checkTOS', 'true');

            if (redirectTo) {
              navigate(redirectTo);
            } else {
              redirectToRole(session.user?.role ?? AuthRole.JOBSEEKER);
            }
          } else if (response.data && response.data.isExternalApply) {
            navigate(`${routes.FORGOT_PASSWORD}?email=${email}`, { state: { isExternalApply: true } });
            return;
          }
        } else if (!isEmpty(session)) {
          if (redirectTo) {
            navigate(redirectTo);
          } else {
            redirectToRole(session.user?.role as AuthRole);
          }
        } else {
          navigate(`/${routes.LOGIN}`);
        }
      },
      signOut: async () => {
        setLoading(true);
        setLabel('Signing out...');

        // Clear employer job draft before clearing session
        if (session?.user?.id) {
          try {
            window.sessionStorage.removeItem(`employer_job_draft_${session.user.id}`);
          } catch (error) {
            console.warn('Error clearing job draft on logout:', error);
          }
        }

        const response = await authLogout({
          token: session?.auth?.token ?? '',
        });
        setLoading(false);

        if (response.error) {
          console.error(response.error);
        } else {
          setSession({});
          setLocalStorage({});
          navigate(`/${routes.LOGIN}`);
        }
      },
      refreshToken,
      createAccount: async (auth: AuthCreateDto) => await authCreate(auth),
      verifyOTP: async (email: string, otp: string) => await authVerifyOTP({ email, otp } as AuthVerifyOtpDto),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      session,
      setSession,
      authLogin,
      authRefresh,
      setLocalStorage,
      navigate,
      setLoading,
      setLabel,
      authLogout,
      authCreate,
      authVerifyOTP,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
